import { useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { DebtAlerts, DebtGuarantee, DebtInstrument, DebtPortfolioStats, DebtServiceForecast, ServiceScheduleItem } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

export function Debt() {
  const { persona } = useAuth();
  const isOfficer = (persona?.roles.includes('DEBT_OFFICER') || persona?.roles.includes('PS_FINANCE')) ?? false;

  const [showRegister, setShowRegister] = useState(false);
  const [showGuarantee, setShowGuarantee] = useState(false);
  const [disburseTarget, setDisburseTarget] = useState<DebtInstrument | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [sessionInstruments, setSessionInstruments] = useState<DebtInstrument[]>(() => {
    const raw = sessionStorage.getItem('govfin.debtInstruments');
    return raw ? (JSON.parse(raw) as DebtInstrument[]) : [];
  });

  const statsQ = useApiQuery<DebtPortfolioStats>('/api/v1/debt/portfolio/stats');
  const forecastQ = useApiQuery<DebtServiceForecast>('/api/v1/debt/service-forecast?horizonDays=365');
  const alertsQ = useApiQuery<DebtAlerts>(isOfficer ? '/api/v1/debt/alerts' : null);
  const guaranteesQ = useApiQuery<{ items: DebtGuarantee[] }>('/api/v1/debt/guarantees');

  const persistInstruments = (items: DebtInstrument[]) => {
    setSessionInstruments(items);
    sessionStorage.setItem('govfin.debtInstruments', JSON.stringify(items));
  };

  const act = async (item: ServiceScheduleItem, action: 'voucher' | 'paid') => {
    setActionError(null);
    try {
      await api.post(`/api/v1/debt/schedules/${item.id}/${action}`);
      forecastQ.reload();
      alertsQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : `${action} failed`);
    }
  };

  return (
    <div>
      <PageHeader
        title="Public Debt"
        subtitle="Every loan and security the Republic owes — service schedule, T-30 alerts, and the currency/maturity profile"
        actions={
          isOfficer ? (
            <div className="flex gap-2">
              <button className="btn-secondary" onClick={() => setShowGuarantee(true)}>
                + Guarantee
              </button>
              <button className="btn-primary" onClick={() => setShowRegister(true)}>
                + Register instrument
              </button>
            </div>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {statsQ.loading ? (
        <Spinner />
      ) : statsQ.error ? (
        <div className="mb-6">
          <ErrorNote message={statsQ.error} />
        </div>
      ) : statsQ.data ? (
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Outstanding principal" value={scr(statsQ.data.outstandingPrincipal)} hint="Active + fully-disbursed instruments" />
          <StatCard label="Average time to maturity" value={`${statsQ.data.averageTimeToMaturityYears} yrs`} />
          {Object.entries(statsQ.data.currencyMix)
            .slice(0, 2)
            .map(([ccy, pct]) => (
              <StatCard key={ccy} label={`${ccy} share`} value={`${pct}%`} hint="Of outstanding principal" />
            ))}
        </div>
      ) : null}

      {sessionInstruments.length > 0 ? (
        <div className="mb-6">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Instruments registered this session</h2>
          <p className="mb-2 text-xs text-slate-400">
            There is no GET /instruments listing endpoint yet — this app remembers what you register in this browser tab so you can record
            disbursements against it.
          </p>
          <div className="card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Reference</th>
                  <th className="table-head">Type</th>
                  <th className="table-head">Creditor</th>
                  <th className="table-head text-right">Principal</th>
                  <th className="table-head">Status</th>
                  {isOfficer ? <th className="table-head" /> : null}
                </tr>
              </thead>
              <tbody>
                {sessionInstruments.map((i) => (
                  <tr key={i.id}>
                    <td className="table-cell font-medium">{i.reference}</td>
                    <td className="table-cell text-xs">{i.type.replaceAll('_', ' ')}</td>
                    <td className="table-cell">{i.creditor}</td>
                    <td className="table-cell text-right tabular-nums">
                      {i.currency} {scr(i.principal)}
                    </td>
                    <td className="table-cell">
                      <StatusBadge status={i.status} />
                    </td>
                    {isOfficer ? (
                      <td className="table-cell text-right">
                        <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setDisburseTarget(i)}>
                          Record disbursement
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {isOfficer ? (
        <div className="mb-6 grid gap-4 lg:grid-cols-2">
          <div>
            <h2 className="mb-2 text-sm font-bold text-slate-700">Due within 30 days</h2>
            <div className="card overflow-x-auto">
              {alertsQ.loading ? (
                <Spinner />
              ) : alertsQ.error ? (
                <ErrorNote message={alertsQ.error} />
              ) : (alertsQ.data?.dueWithin30Days.length ?? 0) === 0 ? (
                <EmptyState title="Nothing due within 30 days" />
              ) : (
                <ScheduleTable items={alertsQ.data!.dueWithin30Days} onAct={act} />
              )}
            </div>
          </div>
          <div>
            <h2 className="mb-2 text-sm font-bold text-slate-700">Overdue — escalated to PS Finance</h2>
            <div className="card overflow-x-auto">
              {alertsQ.loading ? (
                <Spinner />
              ) : alertsQ.error ? (
                <ErrorNote message={alertsQ.error} />
              ) : (alertsQ.data?.overdueEscalatedToPsFinance.length ?? 0) === 0 ? (
                <EmptyState title="No overdue debt service" />
              ) : (
                <ScheduleTable items={alertsQ.data!.overdueEscalatedToPsFinance} onAct={act} />
              )}
            </div>
          </div>
        </div>
      ) : null}

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Debt service schedule — next 12 months</h2>
        {forecastQ.data ? (
          <p className="mb-2 text-xs text-slate-500">
            Principal due {scr(forecastQ.data.totals.principal)} · Interest due {scr(forecastQ.data.totals.interest)}
          </p>
        ) : null}
        <div className="card overflow-x-auto">
          {forecastQ.loading ? (
            <Spinner />
          ) : forecastQ.error ? (
            <ErrorNote message={forecastQ.error} />
          ) : (forecastQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No scheduled debt service" />
          ) : (
            <ScheduleTable items={forecastQ.data!.items} onAct={isOfficer ? act : undefined} />
          )}
        </div>
      </div>

      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-700">Government guarantees</h2>
        </div>
        <div className="card overflow-x-auto">
          {guaranteesQ.loading ? (
            <Spinner />
          ) : guaranteesQ.error ? (
            <ErrorNote message={guaranteesQ.error} />
          ) : (guaranteesQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No guarantees issued" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Beneficiary entity</th>
                  <th className="table-head text-right">Amount</th>
                  <th className="table-head">Status</th>
                </tr>
              </thead>
              <tbody>
                {guaranteesQ.data!.items.map((g) => (
                  <tr key={g.id}>
                    <td className="table-cell">{g.beneficiaryEntity}</td>
                    <td className="table-cell text-right tabular-nums">{scr(g.amount)}</td>
                    <td className="table-cell">
                      <StatusBadge status={g.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showRegister ? (
        <RegisterInstrumentModal
          onClose={() => setShowRegister(false)}
          onDone={(instrument) => {
            setShowRegister(false);
            persistInstruments([instrument, ...sessionInstruments]);
            statsQ.reload();
            forecastQ.reload();
          }}
        />
      ) : null}
      {disburseTarget ? (
        <DisburseModal
          instrument={disburseTarget}
          onClose={() => setDisburseTarget(null)}
          onDone={(updated) => {
            setDisburseTarget(null);
            persistInstruments(sessionInstruments.map((i) => (i.id === updated.instrumentId ? { ...i, status: updated.status } : i)));
          }}
        />
      ) : null}
      {showGuarantee ? (
        <GuaranteeModal
          onClose={() => setShowGuarantee(false)}
          onDone={() => {
            setShowGuarantee(false);
            guaranteesQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ScheduleTable({ items, onAct }: { items: ServiceScheduleItem[]; onAct?: (item: ServiceScheduleItem, action: 'voucher' | 'paid') => void }) {
  return (
    <table className="w-full">
      <thead>
        <tr>
          <th className="table-head">Instrument</th>
          <th className="table-head">Due</th>
          <th className="table-head text-right">Principal</th>
          <th className="table-head text-right">Interest</th>
          <th className="table-head">Status</th>
          {onAct ? <th className="table-head" /> : null}
        </tr>
      </thead>
      <tbody>
        {items.map((i) => (
          <tr key={i.id}>
            <td className="table-cell text-xs">
              {i.instrument?.reference} · {i.instrument?.creditor}
            </td>
            <td className="table-cell text-slate-500">{formatDate(i.dueDate)}</td>
            <td className="table-cell text-right tabular-nums">{scr(i.principalDue)}</td>
            <td className="table-cell text-right tabular-nums">{scr(i.interestDue)}</td>
            <td className="table-cell">
              <StatusBadge status={i.status} />
            </td>
            {onAct ? (
              <td className="table-cell text-right">
                {i.status === 'PROJECTED' || i.status === 'OVERDUE' ? (
                  <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => onAct(i, 'voucher')}>
                    Raise voucher
                  </button>
                ) : i.status === 'VOUCHERED' ? (
                  <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => onAct(i, 'paid')}>
                    Mark paid
                  </button>
                ) : null}
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RegisterInstrumentModal({ onClose, onDone }: { onClose: () => void; onDone: (instrument: DebtInstrument) => void }) {
  const [form, setForm] = useState({
    reference: '',
    type: 'EXTERNAL_LOAN' as DebtInstrument['type'],
    creditor: '',
    currency: 'USD',
    principal: '',
    ratePercent: '',
    tenorYears: '',
    gracePeriodYears: '',
  });
  const [schedule, setSchedule] = useState<Array<{ dueDate: string; principalDue: string; interestDue: string }>>([
    { dueDate: '', principalDue: '', interestDue: '' },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const instrument = await api.post<DebtInstrument>('/api/v1/debt/instruments', {
        reference: form.reference,
        type: form.type,
        creditor: form.creditor,
        currency: form.currency,
        principal: Number.parseFloat(form.principal),
        terms: {
          ratePercent: Number.parseFloat(form.ratePercent),
          tenorYears: Number.parseFloat(form.tenorYears),
          ...(form.gracePeriodYears ? { gracePeriodYears: Number.parseFloat(form.gracePeriodYears) } : {}),
        },
        schedule: schedule
          .filter((s) => s.dueDate && s.principalDue)
          .map((s) => ({
            dueDate: s.dueDate,
            principalDue: Number.parseFloat(s.principalDue),
            interestDue: Number.parseFloat(s.interestDue || '0'),
          })),
      });
      onDone(instrument);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register instrument');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register debt instrument" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Reference">
            <input className="field-input" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="e.g. IBRD-9451-SC" />
          </Field>
          <Field label="Type">
            <select className="field-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as DebtInstrument['type'] })}>
              <option value="EXTERNAL_LOAN">External loan</option>
              <option value="DOMESTIC_LOAN">Domestic loan</option>
              <option value="TBILL">Treasury bill</option>
              <option value="TBOND">Treasury bond</option>
            </select>
          </Field>
          <Field label="Currency (ISO)">
            <input className="field-input" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} maxLength={3} />
          </Field>
        </div>
        <Field label="Creditor">
          <input className="field-input" value={form.creditor} onChange={(e) => setForm({ ...form, creditor: e.target.value })} placeholder="e.g. World Bank (IBRD)" />
        </Field>
        <div className="grid grid-cols-4 gap-3">
          <Field label="Principal">
            <input className="field-input" type="number" min="0" step="0.01" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} />
          </Field>
          <Field label="Rate %">
            <input className="field-input" type="number" min="0" max="50" step="0.01" value={form.ratePercent} onChange={(e) => setForm({ ...form, ratePercent: e.target.value })} />
          </Field>
          <Field label="Tenor (years)">
            <input className="field-input" type="number" min="0" step="0.5" value={form.tenorYears} onChange={(e) => setForm({ ...form, tenorYears: e.target.value })} />
          </Field>
          <Field label="Grace (years, optional)">
            <input className="field-input" type="number" min="0" step="0.5" value={form.gracePeriodYears} onChange={(e) => setForm({ ...form, gracePeriodYears: e.target.value })} />
          </Field>
        </div>
        <div>
          <div className="field-label">Service schedule</div>
          {schedule.map((row, idx) => (
            <div key={idx} className="mb-2 flex gap-2">
              <input className="field-input" type="date" value={row.dueDate} onChange={(e) => setSchedule(schedule.map((r, i) => (i === idx ? { ...r, dueDate: e.target.value } : r)))} />
              <input
                className="field-input"
                type="number"
                placeholder="Principal due"
                value={row.principalDue}
                onChange={(e) => setSchedule(schedule.map((r, i) => (i === idx ? { ...r, principalDue: e.target.value } : r)))}
              />
              <input
                className="field-input"
                type="number"
                placeholder="Interest due"
                value={row.interestDue}
                onChange={(e) => setSchedule(schedule.map((r, i) => (i === idx ? { ...r, interestDue: e.target.value } : r)))}
              />
              <button className="btn-secondary !px-2.5" onClick={() => setSchedule(schedule.filter((_, i) => i !== idx))}>
                ✕
              </button>
            </div>
          ))}
          <button className="btn-secondary !py-1.5 text-xs" onClick={() => setSchedule([...schedule, { dueDate: '', principalDue: '', interestDue: '' }])}>
            + Add instalment
          </button>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !form.reference || !form.creditor || !form.principal || !form.ratePercent || !form.tenorYears || schedule.every((s) => !s.dueDate)}
          >
            {busy ? 'Registering…' : 'Register instrument'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DisburseModal({
  instrument,
  onClose,
  onDone,
}: {
  instrument: DebtInstrument;
  onClose: () => void;
  onDone: (result: { instrumentId: string; status: DebtInstrument['status'] }) => void;
}) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [fxRate, setFxRate] = useState('1');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/debt/instruments/${instrument.id}/disbursements`, {
        date,
        amount: Number.parseFloat(amount),
        fxRate: Number.parseFloat(fxRate),
      });
      onDone({ instrumentId: instrument.id, status: 'FULLY_DISBURSED' });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record disbursement');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Record disbursement — ${instrument.reference}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">Principal {instrument.currency} {scr(instrument.principal)}</p>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Date">
            <input className="field-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label={`Amount (${instrument.currency})`}>
            <input className="field-input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label="FX rate to SCR">
            <input className="field-input" type="number" min="0" step="0.0001" value={fxRate} onChange={(e) => setFxRate(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !amount || !fxRate}>
            {busy ? 'Recording…' : 'Record disbursement'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function GuaranteeModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [beneficiaryEntity, setBeneficiaryEntity] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/debt/guarantees', { beneficiaryEntity, amount: Number.parseFloat(amount) });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to issue guarantee');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Issue government guarantee" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Beneficiary entity">
          <input className="field-input" value={beneficiaryEntity} onChange={(e) => setBeneficiaryEntity(e.target.value)} placeholder="e.g. Air Seychelles Ltd" />
        </Field>
        <Field label="Amount (SCR)">
          <input className="field-input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !beneficiaryEntity || !amount}>
            {busy ? 'Issuing…' : 'Issue guarantee'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
